import { useGetLmsCatalogPlanningsQuery, useAddLmsCatalogPlanningMutation, useUpdateLmsCatalogPlanningMutation, useDeleteLmsCatalogPlanningMutation, useAddLmsPlanningInstructorMutation } from '../../../../api';
import toastService from '../../../../utils/toastService';
import { formatErrorMessage } from '../../../../utils/errorUtils';
import { useLanguage } from '../../../../contexts/LanguageContext';
import useLmsNewsSave from '../../lms-news/hooks/useLmsNewsSave';

// `skip`/yükleme sırasında her render'da yeni literal dönmesin diye modül seviyesinde sabit.
const EMPTY_LIST = { items: [], totalCount: 0 };

/**
 * Eğitim Planlamaları sayfası için veri hook'u. `trainingId` verilirse yalnızca o eğitimin
 * planlamaları, verilmezse (bağımsız Planlamalar sayfasındaki liste ekranı) tüm eğitimlerin
 * planlamaları listelenir — GetList ucu `trainingId`'yi zaten opsiyonel kabul ediyor (doc §8.3).
 * Bkz. docs/LMS-Egitim-Katalogu-ve-Egitmenler-API 2.md §8.
 */
const useTrainingPlanningsData = (trainingId) => {
    const { th } = useLanguage();

    const {
        data: planningsApi,
        isLoading,
        isFetching,
        refetch
    } = useGetLmsCatalogPlanningsQuery({ trainingId, pageNumber: 1, pageSize: 100 }, { refetchOnMountOrArgChange: true });

    const [addMutation, { isLoading: isAdding }] = useAddLmsCatalogPlanningMutation();
    const [updateMutation, { isLoading: isUpdating }] = useUpdateLmsCatalogPlanningMutation();
    const [deleteMutation, { isLoading: isDeleting }] = useDeleteLmsCatalogPlanningMutation();
    const [addPlanningInstructorMutation] = useAddLmsPlanningInstructorMutation();
    const { syncPlanningNews, isSavingNews } = useLmsNewsSave();

    // Add'in cevap şekli backend kaynağından DOĞRULANDI (CreateLmsCatalogPlanningCommandHandler.cs
    // 2026-08-31'de incelendi): `ResponseInfo<LmsCatalogPlanningDto>.Success(dto)` döner, yani JSON
    // cevapta yeni kaydın id'si `data.id` alanındadır (bu repodaki her endpoint'in ortak `data`
    // sarmalayıcısıyla aynı desen). Yine de ekstra bir güvenlik payı olarak birkaç şekil sırayla
    // denenir; hiçbiri sayısal bir id vermezse (beklenmeyen bir durum) eğitmen kopyalama adımı
    // sessizce atlanır, hata fırlatılmaz.
    const extractNewId = (result) => {
        const candidates = [result?.data?.id, result?.id, result?.data, result];
        return candidates.find((value) => typeof value === 'number' && value > 0) ?? null;
    };

    /**
     * @param {object} payload - LmsCatalogPlanning Add/Update gövdesi.
     * @param {number[]} [pendingInstructorUserIds] - Yalnızca ekleme modunda: PlanningForm'da
     *   "Eğitim Kodu" seçilince kataloğun aktif eğitmenlerinden önerilen/kullanıcının düzenlediği
     *   yerel liste (bkz. BE-Talep-LMS-Egitim-Planlamasi.md Talep 1 — backend'de bunu otomatik
     *   yapan bir uç olmadığı için burada FE tarafında, planlama oluşturulduktan hemen sonra
     *   `LmsPlanningInstructor/Add` ile tek tek işlenir).
     */
    const savePlanning = async (payload, pendingInstructorUserIds, newsRequest) => {
        try {
            let planningId = payload.id;
            if (payload.id) {
                await updateMutation(payload).unwrap();
            } else {
                const result = await addMutation(payload).unwrap();
                const newId = extractNewId(result);
                planningId = newId;
                if (newId && Array.isArray(pendingInstructorUserIds) && pendingInstructorUserIds.length > 0) {
                    await Promise.all(pendingInstructorUserIds.map((userId) => addPlanningInstructorMutation({ planningId: newId, userId }).unwrap().catch(() => null)));
                }
            }
            toastService.success(th('trainingPlanning.saveSuccess'), th('common.success'));
            const { newsFailed, photoFailed } = await syncPlanningNews(planningId, newsRequest);
            if (photoFailed) toastService.warn(th('lmsNews.form.photoUploadFailed'), th('common.warning'), 6000);
            const refreshed = await refetch();
            if (newsFailed) {
                toastService.warn(th('lmsNews.planning.newsSaveFailed'), th('common.warning'), 8000);
                return { success: true, reopenPlanning: (refreshed?.data?.items || []).find((row) => row.id === planningId) || null };
            }
            return { success: true };
        } catch (err) {
            toastService.error(formatErrorMessage(err) || th('trainingPlanning.saveError'), th('common.error'));
            return { success: false };
        }
    };

    const deletePlanning = async (id) => {
        try {
            await deleteMutation(id).unwrap();
            toastService.success(th('trainingPlanning.deleteSuccess'), th('common.success'));
            refetch();
            return true;
        } catch (err) {
            toastService.error(formatErrorMessage(err) || th('trainingPlanning.deleteError'), th('common.error'));
            return false;
        }
    };

    return {
        planningsApi: planningsApi || EMPTY_LIST,
        isLoading,
        isFetching,
        isSaving: isAdding || isUpdating || isSavingNews,
        isDeleting,
        savePlanning,
        deletePlanning,
        refetch
    };
};

export default useTrainingPlanningsData;
